/* eslint-disable max-len */
import React, { useState } from 'react';
import { shape, func, arrayOf } from 'prop-types';
import LaundryContainerTitle from './LaundryContainerTitle';
import { CustomProductPod } from './CustomProductPod';
import { InfoDrawer } from './InfoDrawer';
import { DRYER } from '../constants/index';
import LaundryProductsLayout from './LaundryProductsLayout';
import { Symbol } from './laundry/Symbol';
import { AlignedContent } from './laundry/AlignedContent';

const COMMON_PRODUCT_CLASSES = 'sui-w-full md:sui-col-span-3 lg:sui-col-span-2';

/**
 * WasherDryerContainer component displays a washer and dryer product set with an info drawer.
 *
 * @component
 * @param {Object} props - Component props.
 * @param {Array} props.products - Array containing washer and dryer product objects. The first item is the washer, the second is the dryer.
 * @param {Function} props.onSwap - Callback function invoked when a product swap is requested.
 *
 * @returns {JSX.Element} The rendered WasherDryerContainer component.
 */
const WasherDryerContainer = ({
  products,
  onSwap: handleSwap
}) => {
  const [isDrawerOpen, setIsDrawerOpen] = useState(false);

  const handleDrawer = (isOpen) => {
    setIsDrawerOpen(isOpen);
  };

  const [washer, dryer] = products || [];

  return (
    <>
      <div data-testid="washer-dryer-container" className="sui-border-solid sui-border-1 sui-border-primary sui-rounded-base sui-overflow-hidden sui-h-full">
        <div className="sui-flex sui-bg-subtle sui-justify-center sui-p-1">
          <LaundryContainerTitle text="Washer & Dryer Set" onInfoClick={() => handleDrawer(true)} />
        </div>
        <LaundryProductsLayout
          className="sui-gap-6 sui-px-2 sui-py-4 md:sui-px-0 lg:sui-px-0"
          grid={{
            md: 7,
            lg: 5
          }}
        >
          {
            washer && (
              <div data-testid="washer-product" className={`${COMMON_PRODUCT_CLASSES} md:sui-ml-4 lg:sui-ml-6`}>
                <CustomProductPod product={washer} onSwap={handleSwap} />
              </div>
            )
          }
          {
            dryer && (
              <>
                <AlignedContent>
                  <Symbol symbol="+" />
                </AlignedContent>
                <div data-testid="dryer-product" className={`${COMMON_PRODUCT_CLASSES} md:sui-col-start-5 md:-sui-ml-4 lg:-sui-ml-6 lg:sui-col-start-4`}>
                  <CustomProductPod product={dryer} onSwap={handleSwap} />
                </div>
              </>
            )
          }
        </LaundryProductsLayout>
      </div>
      <InfoDrawer type={DRYER} isOpen={isDrawerOpen} onClose={() => handleDrawer(false)} />
    </>
  );
};

WasherDryerContainer.propTypes = {
  products: arrayOf(shape({})),
  onSwap: func.isRequired
};

WasherDryerContainer.defaultProps = {
  products: []
};

WasherDryerContainer.dataModel = CustomProductPod.dataModel;

export default WasherDryerContainer;